import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Footer from "../components/Footer";
import Header from "../components/Header";
import Notification from "../components/Notification";
import { API_URL } from "../config";
import '../styles/SignIn.css';

function AccountActivation() {
    const [amount, setAmount] = useState("");
    const [notification, setNotification] = useState({ type: "", message: "", isVisible: false });
    const [loading, setLoading] = useState(false);
    const [disableSubmitButton, setDisableSubmitButton] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const token = localStorage.getItem("temp_token") ? localStorage.getItem("temp_token") : localStorage.getItem("access_token");
    const [errors, setErrors] = useState({});
    const [profile, setProfile] = useState({
        first_name: "",
        phone_number: "",
        country_code: "254",
    });
    const [mpesaPhoneNumber, setMpesaPhoneNumber] = useState("");
    const [broadcastToken, setBroadcastToken] = useState(null);
    const [successMessage, setSuccessMessage] = useState("");
    const [pollingInterval, setPollingInterval] = useState(null);
    const [logoutTimeout, setLogoutTimeout] = useState(null); // Added this line

    const validate = () => {
        let isValid = true;
        const errors = {};

        if (!amount) {
            errors.amount = "Amount is required.";
            isValid = false;
        } else if (isNaN(amount) || Number(amount) < 100) {
            errors.amount = "Amount must be a number and at least 100 KES.";
            isValid = false;
        }

        // Validate phone number
        const strippedNumber = mpesaPhoneNumber.startsWith("0")
            ? mpesaPhoneNumber.slice(1)
            : mpesaPhoneNumber;

        if (!/^[1-9][0-9]{8}$/.test(strippedNumber)) {
            errors.mpesaPhoneNumber = "Invalid phone number.";
            isValid = false;
        }

        setErrors(errors);
        return isValid;
    };

    // Check payment status periodically
    const checkPaymentStatus = async (startTime = Date.now()) => {
        // Calculate elapsed time
        const elapsed = Date.now() - startTime;

        // Stop polling if 1 minute (60,000 ms) has passed
        if (elapsed > 60000) {
            setNotification({
                type: "danger",
                message: "Payment verification timed out. Please try again",
                isVisible: true
            });
            setLoading(false);
            if (pollingInterval) {
                clearInterval(pollingInterval);
                setPollingInterval(null);
            }
            return;
        }

        try {
            const response = await fetch(`${API_URL}/users/account/renew/status`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Check for 401 first
            if (response.status === 401) {
                console.log("🔴 401 Unauthorized during payment status check");

                // Clear any existing timeouts/intervals
                if (pollingInterval) {
                    clearInterval(pollingInterval);
                    setPollingInterval(null);
                }
                if (logoutTimeout) {
                    clearTimeout(logoutTimeout);
                    setLogoutTimeout(null);
                }

                setNotification({
                    type: "danger",
                    message: "Your session has expired. Please log in again.",
                    isVisible: true
                });

                // Redirect to logout immediately for 401
                setTimeout(() => {
                    navigate("/logout", {
                        state: {
                            from: location,
                            message: "Session expired during payment verification"
                        }
                    });
                }, 2000);
                return;
            }

            const data = await response.json();

            if (response.ok) {
                // Clear any pending logout timeout on success
                if (logoutTimeout) {
                    clearTimeout(logoutTimeout);
                    setLogoutTimeout(null);
                }

                setNotification({
                    type: "success",
                    message: data.message || "Account activated successfully!",
                    isVisible: true
                });
                setSuccessMessage(data.message || "Account activated successfully!");

                // Save the token to localStorage
                if (data.token) {
                    localStorage.setItem("access_token", data.token);
                    localStorage.removeItem("temp_token");
                    localStorage.setItem('user', JSON.stringify(data.user));
                    localStorage.setItem('userRole', data.role);

                    // Clear polling interval
                    if (pollingInterval) {
                        clearInterval(pollingInterval);
                        setPollingInterval(null);
                    }

                    // Redirect after successful activation
                    setTimeout(() => {
                        const from = location.state?.from || { pathname: '/dashboard' };
                        navigate(from.pathname, {
                            replace: true,
                            state: { from: undefined }
                        });
                    }, 2000);
                }
            } else if (response.status === 403) {
                // Payment still pending, continue polling
                console.log("Payment pending...");
            } else {
                // Payment verification failed - log out after 20 seconds
                setNotification({
                    type: "danger",
                    message: "Payment verification failed. You will be logged out in 20 seconds.",
                    isVisible: true
                });

                if (pollingInterval) {
                    clearInterval(pollingInterval);
                    setPollingInterval(null);
                }

                // Set timeout to log out after 20 seconds
                const logoutTimer = setTimeout(() => {
                    navigate("/logout", {
                        state: {
                            from: location,
                            message: "Payment verification failed. Please try again later."
                        }
                    });
                }, 20000); // 20 seconds

                // Store the timer so we can clear it if needed
                setLogoutTimeout(logoutTimer);
            }
        } catch (err) {
            console.error("Error checking payment status:", err);
            setNotification({
                type: "danger",
                message: "Error checking payment status. You will be logged out in 20 seconds.",
                isVisible: true
            });

            if (pollingInterval) {
                clearInterval(pollingInterval);
                setPollingInterval(null);
            }

            // Also set logout timer for network errors
            const logoutTimer = setTimeout(() => {
                navigate("/logout", {
                    state: {
                        from: location,
                        message: "Network error during payment verification"
                    }
                });
            }, 20000);

            setLogoutTimeout(logoutTimer);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        setErrors({});
        setDisableSubmitButton(true); // Disable the submit button to prevent multiple submissions

        if (validate()) {
            try {
                // Prepare the phone number (remove leading 0 if present)
                const strippedNumber = mpesaPhoneNumber.startsWith("0")
                    ? mpesaPhoneNumber.slice(1)
                    : mpesaPhoneNumber;

                const response = await fetch(`${API_URL}/users/account/renew`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        phone_number: strippedNumber,
                        amount: parseFloat(amount)
                    }),
                });

                // Check for 401 first
                if (response.status === 401) {
                    navigate("/logout", { state: { from: location } });
                    return;
                }

                const data = await response.json();

                if (response.ok) {
                    console.log(data);
                    setBroadcastToken(data.broadcastToken);
                    setSuccessMessage(data.message);
                    setNotification({
                        type: "success",
                        message: data.message || "Payment initiated. Please check your phone to complete the transaction.",
                        isVisible: true
                    });
                    setDisableSubmitButton(true); 
                    // Start polling for payment status every 5 seconds
                    const interval = setInterval(() => {
                        checkPaymentStatus();
                    }, 5000);
                    setPollingInterval(interval);
                   // Keep the button disabled during the process
                } else {
                    setNotification({
                        type: "danger",
                        message: data.message || "Payment initiation failed.",
                        isVisible: true
                    });
                    if (data.errors) {
                        setErrors(data.errors);
                    }

                    //
                    const logoutTimer = setTimeout(() => {
                        navigate("/logout", {
                            state: {
                                from: location,
                                message: "Payment verification failed. Please try again later."
                            }
                        });
                    }, 20000);
                    //
                    setLogoutTimeout(logoutTimer);
                    setDisableSubmitButton(false); // Re-enable the button on failure
                }
            } catch (error) {
                console.log("Fetch error:", error);
                setNotification({
                    type: "danger",
                    message: "An error occurred. Please try again later.",
                    isVisible: true
                });
                setDisableSubmitButton(false); // Re-enable the button on error
            } finally {
                setLoading(false);
            }
        } else {
            setLoading(false);
            setDisableSubmitButton(false); // Re-enable the button if validation fails
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch initial profile data
                const profileResponse = await fetch(`${API_URL}/profile/more-details`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (profileResponse.ok) {
                    const profileData = await profileResponse.json();
                    setProfile(profileData);

                    // Set the phone number from profile
                    if (profileData.phone_number) {
                        let phone = profileData.phone_number;
                        // Remove leading 0 or country code if present
                        if (phone.startsWith("0")) {
                            phone = phone.substring(1);
                        } else if (phone.startsWith("254")) {
                            phone = phone.substring(3);
                        }
                        setMpesaPhoneNumber(phone);
                    }
                } else if (profileResponse.status === 401) {
                    navigate("/logout", { state: { from: location } });
                } else {
                    throw new Error("Failed to fetch profile data.");
                }
            } catch (error) {
                console.error("Error fetching profile data:", error);
            }
        };

        fetchData();
    }, [token, navigate, location]);

    // Cleanup all timeouts and intervals on component unmount
    useEffect(() => {
        return () => {
            if (pollingInterval) {
                clearInterval(pollingInterval);
            }
            if (logoutTimeout) {
                clearTimeout(logoutTimeout);
            }
        };
    }, [pollingInterval, logoutTimeout]);

    const handleCloseNotification = () => {
        setNotification({ ...notification, isVisible: false });
    };

    return (
        <div>
            <Header />
            <main>
                <div className="signin-page mb-4">
                    <div className="siginin-bg"></div>
                    <div className="signin-card">
                        <div className="signin-content">
                            <div className="media pb-4 mb-4 is-justify-content-space-between">
                                <div className="media-left">
                                    <Link to="/login"><i className="fa fa-arrow-left"></i> &nbsp; To Log In</Link>
                                </div>
                                <div className="media-right">
                                    <Link to="/"><i className="fa-solid fa-x"></i></Link>
                                </div>
                            </div>
                            <div className="content">
                                <h1 className="sign-in-title">Account Activation</h1>
                                <p className="notification is-info is-light">
                                    To activate your account, you're required to pay a minimum of KES 100 annually as a membership fee.
                                    Once the payment is confirmed, your account will be activated,
                                    <strong> and you'll gain full access to all features and benefits</strong>. <br />
                                    If you have any questions or need assistance with the payment process,
                                    please don't hesitate to contact our support team.
                                </p>

                                {notification.isVisible && (
                                    <Notification
                                        type={notification.type}
                                        message={notification.message}
                                        onClose={handleCloseNotification}
                                    />
                                )}

                                <form onSubmit={handleSubmit}>
                                    {/* Amount */}
                                    <div className="field">
                                        <label className="label">Amount (KES)</label>
                                        <div className="control">
                                            <input
                                                className={`input ${errors.amount ? "is-danger" : ""}`}
                                                type="number"
                                                placeholder="Enter amount (minimum 100 KES)"
                                                value={amount}
                                                onChange={(e) => setAmount(e.target.value)}
                                                required
                                                min="100"
                                            />
                                        </div>
                                        {errors.amount && (
                                            <p className="help is-danger">{errors.amount}</p>
                                        )}
                                    </div>

                                    {/* Phone Number */}
                                    <div className="field">
                                        <label className="label">M-Pesa Phone Number</label>
                                        <div className="control is-flex has-addons">
                                            <span className="button is-static">+254</span>
                                            <input
                                                className={`input ${errors.mpesaPhoneNumber ? "is-danger" : ""}`}
                                                type="text"
                                                placeholder="e.g. 712345678"
                                                value={mpesaPhoneNumber}
                                                onChange={(e) => setMpesaPhoneNumber(e.target.value)}
                                                required
                                                disabled={loading}
                                            />
                                        </div>
                                        {errors.mpesaPhoneNumber && (
                                            <p className="help is-danger">{errors.mpesaPhoneNumber}</p>
                                        )}
                                    </div>

                                    <div className="field mt-5">
                                        <div className="control">
                                            <button
                                                type="submit"
                                                className={`button is-fullwidth primary-bg ${loading ? "is-loading" : ""}`}
                                                disabled={loading || disableSubmitButton}
                                            >
                                                {loading ? "Processing..." : "Activate Account"}
                                            </button>
                                        </div>
                                    </div>
                                </form>

                                {successMessage && !loading && (
                                    <div className="notification is-success mt-4">
                                        <p>{successMessage}</p>
                                        <p>Waiting for payment confirmation...</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
}

export default AccountActivation;